import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './analytics.service';
import { SafeStorage } from '../utils/safe-storage';
import { sessionlessLandingRoute } from '../utils/auth-landing';

/** Where the guest was asked. */
export type GuestHostPromptSurface = 'upload_card' | 'browse_sheet';

/**
 * What this browser remembers about asking. One record per person on this
 * device, not per event, so the cooldown and the close count hold across every
 * event they visit.
 */
interface PromptMemory {
  /** How many times they closed the sheet without starting an event. */
  closes?: number;
  /** They host an event of their own, so they are never asked again. */
  isHost?: boolean;
  /** Per event id: when the bottom sheet was shown there. */
  events?: { [eventId: string]: { browse_sheet?: number } };
}

/** Set when they tap through; read once by the create screens. */
interface PendingStart {
  surface: GuestHostPromptSurface;
  fromEventId: string;
  at: number;
}

const MEMORY_KEY = 'guestHostPrompt';
const PENDING_KEY = 'guestHostPromptStart';
/** A tap that never turned into an event stops counting after a day. */
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Owns every decision about asking a guest "Planning your own event?": whether
 * the feature is on, who qualifies, and the limits.
 *
 * The rules (agreed 2026-10-29):
 *   - Guests only. Anyone who hosts the current event, or has hosted one on
 *     this device, is never asked.
 *   - The upload-card line is a quiet line, not an interruption: it shows on
 *     every guest upload.
 *   - The bottom sheet at most once per person per event. No time limit
 *     between events for now; add one only if it proves annoying.
 *   - Closing the sheet is "not now", not "never": it never stops future asks.
 *     Closes are still counted, for analytics.
 *
 * Kept on the device (localStorage). A guest on a second phone can be asked
 * again there; that is accepted for a prompt this light.
 */
@Injectable({ providedIn: 'root' })
export class GuestHostPromptService {
  private readonly config = environment.guestHostPrompt;

  constructor(private analytics: AnalyticsService, private router: Router) {}

  /** Photos a guest scrolls past before the sheet is considered. */
  get browseAfterPhotos(): number {
    return this.config?.browseAfterPhotos ?? 100;
  }

  /** Galleries smaller than this have nothing to show off; no sheet. */
  get minGalleryPhotos(): number {
    return this.config?.minGalleryPhotos ?? 30;
  }

  /** False when either switch says the feature is off. */
  isEnabled(): boolean {
    if (!this.config?.enabled) return false;
    const flag = this.config.remoteKillSwitchFlag;
    return !(flag && this.analytics.isFlagOn(flag));
  }

  /** Remember that this person hosts, so neither surface asks again. */
  markHost(): void {
    const memory = this.read();
    if (memory.isHost) return;
    memory.isHost = true;
    this.write(memory);
  }

  /** Whether the line may sit on this upload's photos-added card: any guest, every time. */
  canShowUploadLine(eventId: string | number | null, isHostOfEvent: boolean): boolean {
    return this.allowedFor(eventId, isHostOfEvent);
  }

  /** Whether the bottom sheet may open while they browse this event. */
  canShowSheet(eventId: string | number | null, isHostOfEvent: boolean): boolean {
    if (!this.allowedFor(eventId, isHostOfEvent)) return false;
    return !this.read().events?.[String(eventId)]?.browse_sheet;
  }

  recordShown(surface: GuestHostPromptSurface, eventId: string | number): void {
    if (surface === 'browse_sheet') {
      const memory = this.read();
      const id = String(eventId);
      memory.events = memory.events || {};
      memory.events[id] = { ...memory.events[id], browse_sheet: Date.now() };
      this.write(memory);
    }
    this.analytics.capture('guest_host_prompt_shown', { surface, event_id: eventId });
  }

  /** They closed the sheet ("Keep browsing", the X or the backdrop). */
  recordClose(surface: GuestHostPromptSurface, eventId: string | number, how: string): void {
    const memory = this.read();
    memory.closes = (memory.closes || 0) + 1;
    this.write(memory);
    this.analytics.capture('guest_host_prompt_dismissed', {
      surface, event_id: eventId, how, closes: memory.closes,
    });
  }

  /**
   * They tapped through. Quick-join guests can't open the create screen
   * (GuestRestrictionGuard), so they go to the deferred-login start page,
   * which lets them fill the event in first and asks for an account at the
   * end, linking their guest uploads to it on the way.
   */
  start(surface: GuestHostPromptSurface, eventId: string | number): void {
    const pending: PendingStart = { surface, fromEventId: String(eventId), at: Date.now() };
    SafeStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    this.analytics.capture('guest_host_prompt_clicked', { surface, event_id: eventId });

    const isQuickJoinGuest = SafeStorage.getItem('isGuestUser') === 'true';
    this.router.navigateByUrl(isQuickJoinGuest ? sessionlessLandingRoute() : '/create-event');
  }

  /**
   * Properties for `event_created`, once: which prompt sent them, if one did
   * in the last day. Also marks them as a host from now on.
   */
  takeCreatedSource(): Record<string, any> {
    const raw = SafeStorage.getItem(PENDING_KEY);
    SafeStorage.removeItem(PENDING_KEY);
    this.markHost();
    if (!raw) return {};
    try {
      const pending: PendingStart = JSON.parse(raw);
      if (Date.now() - pending.at > PENDING_TTL_MS) return {};
      return { source: 'guest_prompt', prompt_surface: pending.surface, from_event_id: pending.fromEventId };
    } catch {
      return {};
    }
  }

  private allowedFor(eventId: string | number | null, isHostOfEvent: boolean): boolean {
    if (!this.isEnabled() || eventId === null || eventId === undefined || eventId === '') return false;
    if (SafeStorage.getItem('isDemoUser') === 'true' || SafeStorage.getItem('isDemoDirectGallery') === 'true') return false;
    if (isHostOfEvent) {
      this.markHost();
      return false;
    }
    return !this.read().isHost;
  }

  private read(): PromptMemory {
    try {
      return JSON.parse(SafeStorage.getItem(MEMORY_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }

  private write(memory: PromptMemory): void {
    SafeStorage.setItem(MEMORY_KEY, JSON.stringify(memory));
  }
}
